/**
 * Provider payment-account readiness → actionable requirement groups.
 *
 * Presentation only. The canonical item identifiers produced by
 * `GET /api/clinic/payment-readiness` (projected into
 * `/clinic/iyzico/activation-status.blockers`) are never rewritten,
 * re-ordered semantically or invented here — they are merely mapped onto
 * the EXISTING Provider Profile editor that owns each field.
 */

// Which existing Provider Profile section fixes each outstanding item.
export const READINESS_ITEM_SECTION = {
  'field:name': 'identity',
  'field:provider_type': 'identity',
  'field:address': 'contact',
  'field:contact_email': 'contact',
  'field:contact_phone': 'contact',
  'field:legal_name': 'billing',
  'field:contact_name': 'billing',
  'field:contact_surname': 'billing',
  'field:tax_office': 'billing',
  'field:tax_id': 'billing',
  'field:personal_identity_number': 'billing',
  'field:tax_id_format': 'billing',
  'field:personal_identity_number_format': 'billing',
  'banking:iban': 'bank',
  // The destination blockers are fixed by CHOOSING and registering a
  // settlement account, which is the Settlement Accounts card — not by
  // editing a bank-account field. Without these entries the provider read
  // "choose which account iyzico should pay" with no action beside it.
  'banking:destination_unbound': 'settlement',
  'banking:bound_account_missing': 'settlement',
  'banking:currency_ambiguous': 'settlement',
  'banking:bound_account_inactive': 'bank',
  'banking:bound_account_no_iban': 'bank',
  'representative:primary': 'representatives',
};

// Blockers whose canonical code carries a currency suffix resolve to the
// same editor as their prefix.
const READINESS_ITEM_SECTION_PREFIXES = [
  ['banking:settlement_currency_unsupported:', 'settlement'],
  ['banking:bound_account_currency:', 'settlement'],
  ['banking:iban_currency:', 'bank'],
];

export const readinessItemSection = (item) => {
  if (READINESS_ITEM_SECTION[item]) return READINESS_ITEM_SECTION[item];
  const prefixed = READINESS_ITEM_SECTION_PREFIXES.find(
    ([p]) => item?.startsWith?.(p));
  if (prefixed) return prefixed[1];
  if (item?.startsWith?.('document:')) return 'documents';
  return null;
};

// Requirement groups as the Provider understands them. `sections` lists the
// editors that can satisfy the group; `section` is the fallback landing
// target when the group has no outstanding item to point at.
export const REQUIREMENT_GROUPS = [
  { key: 'company_tax', sections: ['identity', 'contact', 'billing'], section: 'billing' },
  { key: 'representative', sections: ['representatives'], section: 'representatives' },
  { key: 'banking', sections: ['bank', 'settlement'], section: 'bank' },
  { key: 'documents', sections: ['documents'], section: 'documents' },
];

// Only provider-fixable requirement families. PSP/config/capability blockers
// are NOT missing provider information and must never be listed as such.
const PROVIDER_FIXABLE_PREFIXES = ['field:', 'banking:', 'representative:', 'document:'];

export const isProviderFixable = (item) =>
  PROVIDER_FIXABLE_PREFIXES.some((p) => String(item).startsWith(p));

/**
 * Group the canonical blockers into the four provider-facing groups.
 * Returns every core group (so completed ones can be shown as done) plus
 * `documents` only when it actually blocks. Items whose canonical code has
 * no mapped editor are returned separately — they are shown without a
 * misleading "Complete" action rather than pointing at the wrong form.
 */
export const groupRequirements = (blockers = []) => {
  const items = (blockers || []).filter(isProviderFixable);
  const groups = REQUIREMENT_GROUPS.map((g) => {
    const groupItems = items.filter((i) => g.sections.includes(readinessItemSection(i) || ''));
    return {
      key: g.key,
      // Land on the editor of the FIRST outstanding item, not merely the
      // right page, so the provider never has to hunt for the field.
      section: (groupItems.length && readinessItemSection(groupItems[0])) || g.section,
      items: groupItems,
      complete: groupItems.length === 0,
    };
  }).filter((g) => g.key !== 'documents' || g.items.length > 0);

  const grouped = new Set(groups.flatMap((g) => g.items));
  return { groups, unmapped: items.filter((i) => !grouped.has(i)) };
};

/** Deep link into the existing Provider Profile editor. */
export const profileEditPath = (section, returnTo, fieldTestId) =>
  `/clinic/profile?section=${encodeURIComponent(section)}${
    returnTo ? `&return=${encodeURIComponent(returnTo)}` : ''
  }${fieldTestId ? `&field=${encodeURIComponent(fieldTestId)}` : ''}`;

// The existing input that satisfies each canonical requirement, so a deep
// link can land ON the field instead of merely on the right card.
export const REQUIREMENT_FIELD_TESTID = {
  'field:legal_name': 'billing-legal-name-input',
  'field:contact_name': 'billing-contact-name-input',
  'field:contact_surname': 'billing-contact-surname-input',
  'field:tax_office': 'billing-tax-office-input',
  'field:tax_id': 'billing-tax-id-input',
  'field:tax_id_format': 'billing-tax-id-input',
  'field:personal_identity_number': 'billing-personal-id-input',
  'field:personal_identity_number_format': 'billing-personal-id-input',
  'field:address': 'contact-address-input',
  'field:contact_email': 'contact-public-email-input',
  'field:contact_phone': 'contact-public-phone-input',
};

export const requirementFieldTestId = (item) => REQUIREMENT_FIELD_TESTID[item] || null;

/** Where a `return=` marker sends the provider back to. */
export const RETURN_TARGETS = {
  integrations: '/clinic/admin?tab=integrations&provider=iyzico',
};
